import * as Yup from 'yup';
import { useFieldArray } from 'react-hook-form';
import { Button, Card, CardContent, Dialog, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { useSaveData } from '../../../hooks/useSaveData';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { CreateTimeAndMaterialButton } from './create-time-and-material.button';
import { DeleteButton } from '../../../components/delete-button.component';
import { Icons } from '../../../assets/icons';
import { CreateBuildingFloorUnitButton } from './create-building-floor-unit.button';
import { useEffect, useRef, useState } from 'react';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { proctorResultApiKeyStore } from '../../../api/proctor-result-api-key-store';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { GenericFileUploadDialog } from '../../../components/file-upload-dialog.component';
import SampleFileForDownload from './sample-file-for-download';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useAuthorize } from '../../../hooks/use-authorize';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { parseErrorMessageRecursive } from '../../../utils/yup-error-parser.util';

interface BuildingUnitsListFormProps {
  projectID: number;
  singleOrMultiLots: any[];
  isTimeAndMaterial: boolean;
  page?: string;
  refetch: () => void;
  syncWithDux: any;
}

export interface SingleOrMultiLot {
  unitID: number;
  buildingID: number | null;
  unitName: string;
  unitNumber: string | null;
  floorID: number | null;
  isCommonArea: boolean | null;
  commonAreaID: number | null;
  address: string;
  city: string | null;
  stateID: number | null;
  zipcode: string | null;
  countyID: number | null;
  municipalityID: number | null;
  jobNumber: string;
  jobsite: string | null;
  proctorResult: string | null;
  disableDelete: boolean | null;
  isUnitCreationCompleted: boolean | null;
  squareFeet: number;
}

export interface LotData {
  projectID: number | null;
  projectBuildings: any; // Replace `any` with the appropriate type if known
  singleOrMultiLots: SingleOrMultiLot[];
}

const proctorResultSchema = Yup.object().shape({
  proctorResultsID: Yup.number().required('Proctor result id is required'),
  unitID: Yup.number().required('Unit id is required'),
  proctorFileName: Yup.string().required('Proctor file name is required'),
});

const lotValidationSchema = Yup.object().shape({
  unitName: Yup.string().required('Lot/Unit is required').max(9, 'Lot/Unit cannot exceed 9 characters'),
  address: Yup.string().required('Address is required').max(31, 'Address cannot exceed 31 characters'),
  jobsite: Yup.string().required('Jobsite is required'),
});

const timeAndMaterialValidationSchema = Yup.object().shape({
  singleOrMultiLots: Yup.array().of(lotValidationSchema),
});

export const TimeAndMaterialEditForm = ({
  projectID,
  isTimeAndMaterial,
  singleOrMultiLots,
  page,
  refetch,
  syncWithDux
}: BuildingUnitsListFormProps) => {
  const form = useReactHookForm({
    defaultValues: {
      projectID,
      singleOrMultiLots,
    },
    yupSchema: timeAndMaterialValidationSchema,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'singleOrMultiLots',
    keyName: 'tID',
  });
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { isLoading, mutateAsync } = useSaveData(projectsApiKeyStore.updateLotInfo());
  const initialJobSites = useRef<string[]>(fields?.map((field: any) => field.jobsite || ''));
  const { data } = useLoadData<ApiResponse<any>>(proctorResultApiKeyStore.getProjectProctorResults(projectID));
  const [isBulkLotDialogOpen, setIsBulkLotDialogOpen] = useState<boolean>(false);
  const [isBulkAddressDialogOpen, setIsBulkAddressDialogOpen] = useState<boolean>(false);
  const { isLoading: isLoadingFile, mutateAsync: UploadFileForBulkLotOrAddressCreation } = useSaveData<LotData>(
    projectsApiKeyStore.uploadFileForBulkLotOrAddressCreation()
  );
  const [transformedValues, setTransformedValues] = useState<any>({});
  const [openDialog, setOpenDialog] = useState(false);

  const { isLoading: isLoadingFileForAddressUpload, mutateAsync: UploadFileForBulkAddressCreation } =
    useSaveData<LotData>(projectsApiKeyStore.uploadFileForBulkAddressCreation());

  const handleLoadCreated = (lot: any) => {
    append(lot);
    initialJobSites.current.push(lot.jobsite || '');
  };

  const handleRemove = (index: number) => {
    remove(index);
    initialJobSites.current.splice(index, 1);
  };

  const transformData = (data: any) => {
    return {
      ...data,
      singleOrMultiLots: data.singleOrMultiLots.map((unit: any) => ({
        ...unit,

        squareFeet: !unit?.squareFeet ? null : Number(unit?.squareFeet),
        proctorResult: !unit.proctorResult
          ? null
          : {
            proctorResultsID: unit.proctorResult.proctorResultsID,
            unitID: unit.unitID,
            proctorFileName: unit.proctorResult.proctorFileName,
          },
      })),
    };
  };

  const handleSubmit = async (values: any) => {
    const transformedData = transformData(values);

    const { type, ...dataWithoutType } = transformedData;
    const hasCreateWOTrue = transformedData?.singleOrMultiLots?.some((item: any) => item?.createWO === true);
    if (transformedData?.singleOrMultiLots?.length > 0 && !hasCreateWOTrue && type == 'with-workorder') {
      showSnackbar(`Please select at least one 'Create Work Order' before saving.`, 'error');
      return;
    }

    if (hasCreateWOTrue && type == 'without-workorder') {
      showSnackbar(`Please do not select 'Create Work Order' before saving.`, 'error');
      return;
    }
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: type == 'with-workorder' } }).then((data: any) => {
      const lots = data?.data?.singleOrMultiLots;
      initialJobSites.current = data?.data?.singleOrMultiLots?.map((field: any) => field.jobsite || '');
      form.setValue('singleOrMultiLots', lots);
    });
  };

  const handleUnitNameChange = (index: number, value: string) => {
    const initialJobSite = initialJobSites?.current[index];

    const jobsite = form.getValues(`singleOrMultiLots.${index}.jobsite`);
    const newJobSite = `${initialJobSite}${value}`?.trim();
    form.setValue(`singleOrMultiLots.${index}.jobsite`, newJobSite);

    if (!value || value?.trim() === "") {
      form.clearErrors(`singleOrMultiLots.${index}.unitName`);
      return;
    }

    // ✅ Check duplicates within form
    const allUnits = form.getValues("singleOrMultiLots")?.map((u: any) => u.unitName?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.unitName`, {
        type: "manual",
        message: "Lot/Unit address already exists.",
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.unitName`);
    }
  };

  const handleJobNumberChange = (index: number, value: string) => {

    // ✅ If empty, just clear error & return
    if (!value || value?.trim() === "") {
      form.clearErrors(`singleOrMultiLots.${index}.jobNumber`);
      return;
    }

    // ✅ Check duplicates within form
    const allUnits = form.getValues("singleOrMultiLots")?.map((u: any) => u?.jobNumber?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.jobNumber`, {
        type: "manual",
        message: "Job number already exists.",
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.jobNumber`);
    }
  };

  const handleAddressChange = (index: number, value: string) => {

    if (!value || value?.trim() === "") {
      form.clearErrors(`singleOrMultiLots.${index}.address`);
      return;
    }

    // ✅ Check duplicates within form
    const allUnits = form.getValues("singleOrMultiLots")?.map((u: any) => u?.address?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.address`, {
        type: "manual",
        message: "Address already exists.",
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.address`);
    }
  };

  const handleJobsiteNameChange = (index: number, value: any) => {
    initialJobSites.current[index] = value;

    if (!value || value?.trim() === "") {
      form.clearErrors(`singleOrMultiLots.${index}.jobsite`);
      return;
    }

    const allUnits = form.getValues("singleOrMultiLots")?.map((u: any) => u?.jobsite?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.jobsite`, {
        type: "manual",
        message: "Jobsite already exists.",
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.jobsite`);
    }
  };

  const handleSubmitBulkLotCreationFileSave = async (file: File | null) => {
    if (!file || !projectID) return;

    try {
      const formData = new FormData();
      formData.append('ProjectID', projectID.toString());
      formData.append('DataFile', file);

      const response = await UploadFileForBulkLotOrAddressCreation({ data: formData });

      const lots = response?.data?.singleOrMultiLots;
      if (Array.isArray(lots)) {
        lots.forEach((lot) => {
          append(lot);
          initialJobSites.current.push(lot.jobsite || '');
        });
      }
    } catch (error) {
      console.error('Bulk lot upload failed:', error);
    } finally {
      setIsBulkLotDialogOpen(false);
    }
  };

  const handleSubmitBulkAddressCreationFileSave = async (file: File | null) => {
    if (!file || !projectID) return;

    try {
      const formData = new FormData();
      formData.append('ProjectID', projectID.toString());
      formData.append('DataFile', file);

      const response = await UploadFileForBulkAddressCreation({ data: formData });

      const lots = response?.data?.singleOrMultiLots;
      form.setValue('singleOrMultiLots', lots);
      initialJobSites.current = lots?.map((field: any) => field.jobsite || '');
    } catch (error) {
      console.log('Bulk Address upload failed:', error);
    } finally {
      setIsBulkAddressDialogOpen(false);
    }
  };

  const closeDialog = async () => {
    const { type, ...dataWithoutType } = transformedValues;
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: false } }).then((data: any) => {
      const lots = data?.data?.singleOrMultiLots;
      initialJobSites.current = data?.data?.singleOrMultiLots?.map((field: any) => field.jobsite || '');
      form.setValue('singleOrMultiLots', lots);
    });
    setOpenDialog(false);
  };

  const handleSubmitConfirm = async () => {
    const { type, ...dataWithoutType } = transformedValues;
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: true } }).then((data: any) => {
      const lots = data?.data?.singleOrMultiLots;
      initialJobSites.current = data?.data?.singleOrMultiLots?.map((field: any) => field.jobsite || '');
      form.setValue('singleOrMultiLots', lots);
    });

    setOpenDialog(false);
  };

  const handleError = (error: any) => {
    const messages = parseErrorMessageRecursive(error);

    let requiredMessages: any[] = [];
    let otherMessages: any[] = [];

    // Classify messages
    Array.isArray(messages) &&
      messages?.forEach((message) => {
        if (message.endsWith('is required')) {
          requiredMessages.push(message?.split(' is required')[0]?.trim());
        } else {
          otherMessages.push(message.trim());
        }
      });

    // Remove duplicates
    requiredMessages = Array.from(new Set(requiredMessages));
    otherMessages = Array.from(new Set(otherMessages));

    // Construct the final message
    const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
    const finalOtherMessage = otherMessages?.join(', ');

    // Combine messages
    const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

    // Show the final formatted message
    showSnackbar(`Validation Error: ${finalMessage}`, 'error');
  };

  const onTypedSubmit = (type: string) =>
    form.handleSubmit(
      (values) => handleSubmit({ ...values, type }),
      handleError
    )();

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  const disabledBuildingPermit = (index: number) => {
    return form.getValues('singleOrMultiLots')[index]?.projectSyncWithDUX;
  };

  return (
    <div>
      <ReactHookForm
        formName="TIME_AND_MATERIAL_FORM"
        onSubmit={handleSubmit}
        formContextProps={form}
        isBusy={isLoading}
      >
        <Card elevation={0}>
          <CardContent style={{ padding: '25px', paddingTop: '30px' }}>
            <Grid container spacing={2}>
              {fields?.map((field: any, index: any) => (
                <Grid key={field.tID} item lg={12} sm={12} xl={12} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item lg={8} sm={8} xl={8} xs={8}>
                      <div style={{ display: 'none' }}>
                        <label>{form.getValues(`singleOrMultiLots.${index}.unitNumber`)}</label>
                      </div>
                      <Grid container spacing={2}>
                        <Grid item lg={2} sm={2} xl={2} xs={2}>
                          <FormInput
                            // disabled={true}
                            name={`singleOrMultiLots.${index}.jobsite`}
                            onChange={(e: any) => handleJobsiteNameChange(index, e)}
                            label={isTimeAndMaterial ? 'Inspection Title' : 'Jobsite'}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressJobsite(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                '',
                                value
                              )
                            }
                          />
                        </Grid>
                        <Grid item lg={2} sm={2} xl={2} xs={2}>
                          <FormInput
                            name={`singleOrMultiLots.${index}.unitName`}
                            label={'Lot/Unit'}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressUnitName(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                value,
                                ''
                              )
                            }
                            onChange={(e: any) => handleUnitNameChange(index, e)}
                          />
                        </Grid>
                        <Grid item lg={2} sm={2} xl={2} xs={2}>
                          <FormInput
                            onChange={(e: any) => handleJobNumberChange(index, e)}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressJobNumber(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                '',
                                '',
                                value
                              )
                            }
                            name={`singleOrMultiLots.${index}.jobNumber`}
                            label={'Builder Job Number'}
                          />
                        </Grid>
                        <Grid item lg={2} sm={2} xl={2} xs={2}>
                          <FormInput
                            name={`singleOrMultiLots.${index}.address`}
                            label={'Address'}
                            onChange={(e: any) => handleAddressChange(index, e)}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressOfAddress(
                                projectID,
                                value,
                                form.getValues(`singleOrMultiLots.${index}.unitID`)
                              )
                            }
                          />
                        </Grid>
                        <Grid item lg={2} sm={2} xl={2} xs={2}>
                          <FormInput name={`singleOrMultiLots.${index}.squareFeet`} label={'Square Feet'} />
                        </Grid>
                        <Grid item lg={2} sm={2} xl={2} xs={2}>
                          <FormAutoCompleteSelectDynamic
                            name={`singleOrMultiLots.${index}.proctorResult`}
                            label="Proctor Result"
                            optionsValueKey="proctorResultsID"
                            optionsLabelKey="proctorFileName"
                            dataItems={!data ? [] : data?.data}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={4} sm={4} xl={4} xs={4}>
                      <Grid
                        container
                        direction="row"
                        sx={{
                          justifyContent: "flex-start",
                          alignItems: "flex-start",
                        }}
                      >
                        <FormCheckbox label="Create WO" name={`singleOrMultiLots.${index}.createWO`} />
                        <FormCheckbox label="Sync With DUX" name={`singleOrMultiLots.${index}.syncWithDUX`} disabled={!disabledBuildingPermit(index)} />
                        <FormInput label="Building Permit#" name={`singleOrMultiLots.${index}.buildingPermit`} styleField={{ width: '180px' }} />
                        {page &&
                          form.getValues(`singleOrMultiLots.${index}.isUnitCreationCompleted`) &&
                          !!form.getValues(`singleOrMultiLots.${index}.unitID`) && (
                            <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_UNIT]}>
                              <IconButton
                                style={{ padding: '10px 2px 5px 15px' }}
                                aria-label="view"
                                onClick={() =>
                                  navigate(`/units/profile/${form.getValues(`singleOrMultiLots.${index}.unitID`)}`)
                                }
                              >
                                <Tooltip title={<Typography variant="body1">View</Typography>}>
                                  <VisibilityIcon className="btnView" />
                                </Tooltip>
                              </IconButton>
                            </GuardedComponent>
                          )}
                        {!!form.getValues(`singleOrMultiLots.${index}.unitID`) && (
                          <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_UNIT]}>
                            {!form.getValues(`singleOrMultiLots.${index}.disableDelete`) ? (
                              <DeleteButton
                                data={undefined}
                                deleteMutationConfig={() =>
                                  projectsApiKeyStore.deleteLot(form.getValues(`singleOrMultiLots.${index}.unitID`))
                                }
                                onDeleted={() => handleRemove(index)}
                              >
                                <>
                                  <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                    <img src={Icons.Delete} alt="DELETE" />
                                  </Tooltip>
                                </>
                              </DeleteButton>
                            ) : (
                              <DeleteButton
                                data={undefined}
                                deleteMutationConfig={() =>
                                  projectsApiKeyStore.deleteLot(form.getValues(`singleOrMultiLots.${index}.unitID`))
                                }
                                onDeleted={() => handleRemove(index)}
                                isDisabled={true}
                              >
                                <>
                                  <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                    <img src={Icons.DisabledDelete} alt="DELETE" />
                                  </Tooltip>
                                </>
                              </DeleteButton>
                            )}
                          </GuardedComponent>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </CardContent>

          <Grid item lg={1} sm={1} xl={1} xs={1}>
            {isTimeAndMaterial ? (
              <CreateTimeAndMaterialButton projectID={projectID} syncWithDux={syncWithDux} onUnitCreated={handleLoadCreated} />
            ) : (
              <CreateBuildingFloorUnitButton
                projectID={projectID}
                onUnitCreated={handleLoadCreated}
                floor={{ floorID: undefined }}
                syncWithDux={syncWithDux}
              />
            )}
          </Grid>
        </Card>

        <Grid
          item
          lg={12}
          sm={12}
          xl={12}
          xs={12}
          style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}
        >

          <>
            <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
              <SampleFileForDownload />
            </GuardedComponent>
            {/* --------------------------------------- */}
            <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
              <Button
                type="button"
                onClick={() => setIsBulkAddressDialogOpen(true)}
                className="btn btn-primary btnIcon"
                variant="text"
                sx={{ p: 0, m: 0, mr: 1 }}
              >
                Bulk Address Upload
              </Button>
            </GuardedComponent>
            <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
              <Button
                type="button"
                onClick={() => setIsBulkLotDialogOpen(true)}
                className="btn btn-primary btnIcon"
                variant="text"
                sx={{ p: 0, m: 0, mr: 1 }}
              >
                Bulk Lot Creation
              </Button>
            </GuardedComponent>
          </>
          <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
            <Button
              type="button"
              disabled={!form.getValues(`singleOrMultiLots`) || form.getValues(`singleOrMultiLots`)?.length === 0}
              className="btn btn-primary btnIcon"
              variant="text"
              style={{
                backgroundColor:
                  !form.getValues(`singleOrMultiLots`) || form.getValues(`singleOrMultiLots`)?.length === 0
                    ? 'gray'
                    : '#e67e22',
              }}
              sx={{ p: 0, m: 0, mr: 1 }}
              onClick={() => onTypedSubmit('without-workorder')}
            >
              Create Lots/Units without workorders
            </Button>
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
            <Button
              type="button"
              disabled={!form.getValues(`singleOrMultiLots`) || form.getValues(`singleOrMultiLots`)?.length === 0}
              className="btn btn-primary btnIcon"
              variant="text"
              style={{
                backgroundColor:
                  !form.getValues(`singleOrMultiLots`) || form.getValues(`singleOrMultiLots`)?.length === 0
                    ? 'gray'
                    : '#239b56',
              }}
              sx={{ p: 0, m: 0, mr: 1 }}
              onClick={() => onTypedSubmit('with-workorder')}
            >
              Create Lots/Units with workorder
            </Button>
          </GuardedComponent>
        </Grid>
      </ReactHookForm>
      {
        openDialog && (
          <Dialog
            open={openDialog}
            className="modalpopup"
            title={'CONFIRM STATUS'}
          // onClose={closeDialog}
          >
            <div>
              <div style={{ padding: '25px 50px', textAlign: 'center' }}>
                <div>Are you sure you want to generate work orders for all the newly added Lot/Units?</div>
                {/* <LoadingIndicator isLoading={isLoadingUnitComplete} /> */}
              </div>
            </div>
            <DialogActionsBar>
              {isReadOnlyPermission && (
                <Button style={{ maxWidth: '140px' }} size='small' type="button" variant="outlined" onClick={() => setOpenDialog(false)}>
                  Cancel
                </Button>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }} className="  ">
                <button
                  className="btn btn-sm btn-primary btn-lightGrey"
                  style={{
                    backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                    color: isReadOnlyPermission ? 'black' : '#fff',
                    cursor: 'pointer', padding: 2, marginRight: 6
                  }}
                  disabled={isReadOnlyPermission}
                  onClick={closeDialog}
                  autoFocus
                >
                  No
                </button>

                <button
                  className="btn btn-sm btn-primary  btn-delete"
                  style={{
                    backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                    color: isReadOnlyPermission ? 'black' : '#fff',
                    cursor: 'pointer', padding: 2
                  }}
                  type="button"
                  onClick={handleSubmitConfirm}
                  disabled={isLoading || isReadOnlyPermission}
                >
                  Yes
                </button>
              </div>
            </DialogActionsBar>
          </Dialog>
        )
      }
      {
        isBulkLotDialogOpen && (
          <GenericFileUploadDialog
            title="Upload File For Bulk Lot Creation"
            defaultData={{}}
            fileRequestArgs={{}}
            isLoading={isLoadingFile}
            onSubmit={(formData) => handleSubmitBulkLotCreationFileSave(formData)}
            onClose={() => setIsBulkLotDialogOpen(false)}
            acceptedFileTypes={{
              'text/csv': ['.csv'],
              'application/vnd.ms-excel': ['.xls'],
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            }}
          />
        )
      }
      {
        isBulkAddressDialogOpen && (
          <GenericFileUploadDialog
            title="Upload File For Bulk Address Creation"
            defaultData={{}}
            fileRequestArgs={{}}
            isLoading={isLoadingFileForAddressUpload}
            onSubmit={(formData) => handleSubmitBulkAddressCreationFileSave(formData)}
            onClose={() => setIsBulkAddressDialogOpen(false)}
            acceptedFileTypes={{
              'text/csv': ['.csv'],
              'application/vnd.ms-excel': ['.xls'],
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            }}
          />
        )
      }
    </div >
  );
};
